import { Link, createFileRoute, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useState } from 'react';

import { AuthenticatedMobileShell } from '../components/AuthenticatedMobileShell.tsx';
import { ConfirmModal } from '../components/ConfirmModal.tsx';
import { StatusMessage } from '../components/StatusMessage.tsx';
import { WorkspaceContainer } from '../components/WorkspaceLayout.tsx';
import { useMe } from '../lib/auth.ts';
import { useCounterparties, useDeleteCounterparty } from '../lib/counterparties.ts';
import { CUSTOMER_DELETE_ERROR, type Counterparty } from '../../shared/counterparty.ts';

export const Route = createFileRoute('/counterparties')({
  validateSearch: (search: Record<string, unknown>): { deleted?: boolean; q?: string } => {
    const q = typeof search.q === 'string' && search.q.trim() !== '' ? search.q.trim() : undefined;
    const deleted = search.deleted === true || search.deleted === 'true' ? true : undefined;
    return { deleted, q };
  },
  component: CounterpartiesRoute,
});

export function CounterpartiesRoute(): React.ReactElement {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const me = useMe();

  if (pathname !== '/counterparties') {
    return <Outlet />;
  }

  if (me.isLoading) {
    return <CenteredMessage>Loading…</CenteredMessage>;
  }

  if (me.data == null) {
    return <LoginScreen />;
  }

  return (
    <AuthenticatedMobileShell currentPath="/counterparties">
      <AuthenticatedCounterparties />
    </AuthenticatedMobileShell>
  );
}

function AuthenticatedCounterparties(): React.ReactElement {
  const routeSearch = Route.useSearch();
  const navigate = useNavigate();
  const [search, setSearch] = useState(routeSearch.q ?? '');
  const [deleteMessage, setDeleteMessage] = useState<string | null>(
    routeSearch.deleted ? 'Customer deleted.' : null,
  );
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [selectedForDelete, setSelectedForDelete] = useState<Counterparty | null>(null);
  const counterpartiesQuery = useCounterparties(search);
  const deleteCounterparty = useDeleteCounterparty();

  async function confirmDelete(): Promise<void> {
    if (!selectedForDelete) return;
    setDeleteMessage(null);
    setDeleteError(null);
    try {
      await deleteCounterparty.mutateAsync(selectedForDelete.id);
      setSelectedForDelete(null);
      setDeleteMessage('Customer deleted.');
    } catch {
      setDeleteError(CUSTOMER_DELETE_ERROR);
    }
  }

  const counterparties = counterpartiesQuery.data?.counterparties ?? [];
  const isSearching = search.trim() !== '';

  return (
    <div className="min-h-screen">
      <WorkspaceContainer as="main" className="py-8">
        <div className="space-y-6">
          <h1 className="font-serif text-3xl italic text-ink sm:text-4xl">Customers</h1>
          <CounterpartiesToolbar
            search={search}
            onSearchChange={(value) => {
              setSearch(value);
              void navigate({
                to: '/counterparties',
                search: value.trim() ? { q: value.trim() } : {},
                replace: true,
              });
            }}
          />

          {deleteMessage ? <StatusMessage tone="success">{deleteMessage}</StatusMessage> : null}
          {deleteError ? <StatusMessage tone="error">{deleteError}</StatusMessage> : null}

          {counterpartiesQuery.isPending ? (
            <p className="text-sm text-ink-muted">Loading…</p>
          ) : null}
          {counterpartiesQuery.isError ? (
            <StatusMessage tone="error">
              Could not load customers. Try again or check the logs.
            </StatusMessage>
          ) : null}
          {counterpartiesQuery.isSuccess && counterparties.length === 0 ? (
            <EmptyState>{isSearching ? 'No customers found.' : 'No customers saved.'}</EmptyState>
          ) : null}
          {counterparties.length > 0 ? (
            <CustomerList
              counterparties={counterparties}
              onDelete={(counterparty) => {
                setDeleteMessage(null);
                setDeleteError(null);
                setSelectedForDelete(counterparty);
              }}
            />
          ) : null}
        </div>
      </WorkspaceContainer>
      <ConfirmModal
        open={selectedForDelete !== null}
        title="Delete customer"
        confirmLabel="Delete customer"
        confirming={deleteCounterparty.isPending}
        onCancel={() => setSelectedForDelete(null)}
        onConfirm={() => {
          void confirmDelete();
        }}
      >
        <p>
          Delete <span className="font-medium">{selectedForDelete?.name}</span>? This cannot be
          undone.
        </p>
      </ConfirmModal>
    </div>
  );
}

function CounterpartiesToolbar({
  search,
  onSearchChange,
}: {
  search: string;
  onSearchChange: (value: string) => void;
}): React.ReactElement {
  return (
    <form
      role="search"
      aria-label="Customers search"
      className="flex min-w-0 flex-col gap-4 border-b border-line pb-6 sm:flex-row sm:items-end sm:justify-between"
      onSubmit={(event) => event.preventDefault()}
    >
      <div className="w-full max-w-md min-w-0 space-y-1.5">
        <label
          className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-ink-muted"
          htmlFor="customer-search"
        >
          Search
        </label>
        <input
          id="customer-search"
          type="search"
          value={search}
          onChange={(event) => onSearchChange(event.currentTarget.value)}
          className="block min-h-11 w-full min-w-0 rounded-lg border border-line bg-paper-soft px-3 py-2 text-sm text-ink shadow-[inset_0_1px_0_rgba(255,255,255,0.4)] outline-none transition placeholder:text-ink-muted focus:border-ochre focus:ring-2 focus:ring-ochre/30"
        />
      </div>
      <Link
        to="/counterparties/new"
        className="inline-flex min-h-11 w-full shrink-0 items-center justify-center rounded-full bg-ochre px-5 py-2 text-sm font-semibold text-paper-soft shadow-[0_8px_20px_-12px_rgba(176,119,43,0.85)] transition hover:bg-ochre-deep sm:w-auto"
      >
        New Customer
      </Link>
    </form>
  );
}

function CustomerList({
  counterparties,
  onDelete,
}: {
  counterparties: Counterparty[];
  onDelete: (counterparty: Counterparty) => void;
}): React.ReactElement {
  return (
    <>
      <CustomerDesktopTable counterparties={counterparties} onDelete={onDelete} />
      <CustomerMobileCards counterparties={counterparties} onDelete={onDelete} />
    </>
  );
}

function CustomerDesktopTable({
  counterparties,
  onDelete,
}: {
  counterparties: Counterparty[];
  onDelete: (counterparty: Counterparty) => void;
}): React.ReactElement {
  return (
    <div className="hidden rounded-2xl border border-line bg-paper-soft shadow-[0_12px_36px_-28px_rgba(31,27,21,0.45)] md:block">
      <table aria-label="Customers" className="min-w-full text-sm">
        <thead className="border-b border-line bg-paper-deep/60 text-ink-muted">
          <tr>
            <th
              scope="col"
              className="px-5 py-3 text-left text-[10px] font-semibold uppercase tracking-[0.22em]"
            >
              Name
            </th>
            <th
              scope="col"
              className="px-5 py-3 text-left text-[10px] font-semibold uppercase tracking-[0.22em]"
            >
              Email
            </th>
            <th
              scope="col"
              className="px-5 py-3 text-left text-[10px] font-semibold uppercase tracking-[0.22em]"
            >
              Created
            </th>
            <th
              scope="col"
              className="px-5 py-3 text-right text-[10px] font-semibold uppercase tracking-[0.22em]"
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line-soft">
          {counterparties.map((counterparty) => (
            <CustomerDesktopRow
              key={counterparty.id}
              counterparty={counterparty}
              onDelete={onDelete}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CustomerDesktopRow({
  counterparty,
  onDelete,
}: {
  counterparty: Counterparty;
  onDelete: (counterparty: Counterparty) => void;
}): React.ReactElement {
  return (
    <tr className="transition hover:bg-paper-deep/40">
      <td className="break-words px-5 py-4 align-top">
        <Link
          to="/counterparties/$id"
          params={{ id: counterparty.id }}
          className="font-serif text-lg italic leading-tight text-ink transition hover:text-ochre-deep"
        >
          {counterparty.name}
        </Link>
      </td>
      <td className="break-words px-5 py-4 align-top text-ink-soft">{counterparty.email ?? '—'}</td>
      <td className="px-5 py-4 align-top text-xs uppercase tracking-[0.16em] text-ink-muted">
        {formatDate(counterparty.createdAt)}
      </td>
      <td className="px-5 py-4 align-top text-right">
        <div className="flex justify-end gap-2">
          <Link
            aria-label={`Edit ${counterparty.name}`}
            to="/counterparties/$id/edit"
            params={{ id: counterparty.id }}
            className="inline-flex min-h-11 items-center justify-center rounded-full border border-line bg-paper px-3.5 py-1.5 text-xs font-semibold uppercase tracking-[0.08em] text-ink transition hover:border-ochre/40 hover:bg-paper-deep"
          >
            Edit
          </Link>
          <button
            type="button"
            aria-label={`Delete ${counterparty.name}`}
            onClick={() => onDelete(counterparty)}
            className="inline-flex min-h-11 items-center justify-center rounded-full border border-clay/40 bg-paper px-3.5 py-1.5 text-xs font-semibold uppercase tracking-[0.08em] text-clay transition hover:border-clay/70 hover:bg-clay/10"
          >
            Delete
          </button>
        </div>
      </td>
    </tr>
  );
}

function CustomerMobileCards({
  counterparties,
  onDelete,
}: {
  counterparties: Counterparty[];
  onDelete: (counterparty: Counterparty) => void;
}): React.ReactElement {
  return (
    <section aria-label="Customer cards" className="space-y-3 md:hidden">
      {counterparties.map((counterparty) => (
        <CustomerMobileCard key={counterparty.id} counterparty={counterparty} onDelete={onDelete} />
      ))}
    </section>
  );
}

function CustomerMobileCard({
  counterparty,
  onDelete,
}: {
  counterparty: Counterparty;
  onDelete: (counterparty: Counterparty) => void;
}): React.ReactElement {
  return (
    <article
      aria-label={counterparty.name}
      className="rounded-2xl border border-line bg-paper-soft p-4 shadow-[0_8px_20px_-18px_rgba(31,27,21,0.45)]"
    >
      <div className="space-y-1.5">
        <Link
          to="/counterparties/$id"
          params={{ id: counterparty.id }}
          className="inline-flex min-h-11 items-center break-words font-serif text-lg italic leading-tight text-ink transition hover:text-ochre-deep"
        >
          {counterparty.name}
        </Link>
        <p className="break-words text-sm text-ink-soft">{counterparty.email ?? '—'}</p>
        <p className="text-[11px] uppercase tracking-[0.16em] text-ink-muted">
          Created {formatDate(counterparty.createdAt)}
        </p>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Link
          to="/counterparties/$id/edit"
          params={{ id: counterparty.id }}
          className="inline-flex min-h-11 items-center justify-center rounded-full border border-line bg-paper px-4 text-xs font-semibold uppercase tracking-[0.08em] text-ink transition hover:border-ochre/40 hover:bg-paper-deep"
        >
          Edit
        </Link>
        <button
          type="button"
          onClick={() => onDelete(counterparty)}
          className="inline-flex min-h-11 items-center justify-center rounded-full border border-clay/40 bg-paper px-4 text-xs font-semibold uppercase tracking-[0.08em] text-clay transition hover:border-clay/70 hover:bg-clay/10"
        >
          Delete
        </button>
      </div>
    </article>
  );
}

export function CounterpartiesHeader({
  title,
  actionLink,
  backLink = { to: '/' },
}: {
  title: string;
  actionLink?: CounterpartiesRouteLink;
  backLink?: CounterpartiesRouteLink;
}): React.ReactElement {
  return (
    <div className="flex min-w-0 flex-col gap-4 border-b border-line pb-6 sm:flex-row sm:items-end sm:justify-between">
      <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center">
        <Link
          {...backLink}
          className="inline-flex min-h-11 w-fit items-center justify-center gap-1.5 rounded-full border border-line bg-paper px-3.5 py-2 text-sm font-medium text-ink transition hover:border-ochre/40 hover:bg-paper-deep"
        >
          <span aria-hidden="true">←</span>
          Back
        </Link>
        <h1 className="min-w-0 break-words font-serif text-3xl italic text-ink sm:text-4xl">
          {title}
        </h1>
      </div>
      {actionLink ? (
        <Link
          {...actionLink}
          className="inline-flex min-h-11 shrink-0 items-center justify-center rounded-full bg-ochre px-5 py-2 text-sm font-semibold text-paper-soft shadow-[0_8px_20px_-12px_rgba(176,119,43,0.85)] transition hover:bg-ochre-deep"
        >
          New Customer
        </Link>
      ) : null}
    </div>
  );
}

type CounterpartiesRouteLink =
  | { to: '/' }
  | { to: '/counterparties' }
  | { to: '/counterparties/new' }
  | { to: '/counterparties/$id'; params: { id: string } };

function EmptyState({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div className="flex flex-col items-center gap-4 rounded-2xl border border-dashed border-line bg-paper-soft/60 px-6 py-12 text-center">
      <p className="font-serif text-lg italic text-ink-soft">{children}</p>
      <Link
        to="/counterparties/new"
        className="inline-flex min-h-11 items-center justify-center rounded-full bg-ochre px-5 py-2 text-sm font-semibold text-paper-soft shadow-[0_8px_20px_-12px_rgba(176,119,43,0.85)] transition hover:bg-ochre-deep"
      >
        New Customer
      </Link>
    </div>
  );
}

export function LoginScreen(): React.ReactElement {
  return (
    <CenteredFrame>
      <div className="flex flex-col items-center gap-6 rounded-2xl border border-line bg-paper-soft px-10 py-12 shadow-[0_18px_60px_-30px_rgba(31,27,21,0.35)]">
        <div className="flex flex-col items-center gap-2">
          <h1 className="font-serif text-4xl font-medium italic leading-tight text-ink">
            Invoice Generator
          </h1>
          <p className="max-w-xs text-balance text-sm text-ink-soft">
            A small, private workshop for your invoices. Sign in to continue.
          </p>
        </div>
        <a
          href="/auth/github/login"
          className="inline-flex min-h-11 items-center justify-center rounded-full bg-ochre px-6 py-2.5 text-sm font-semibold text-paper-soft shadow-[0_8px_20px_-10px_rgba(176,119,43,0.8)] transition hover:bg-ochre-deep"
        >
          Sign in with GitHub
        </a>
      </div>
    </CenteredFrame>
  );
}

export function CenteredFrame({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div className="flex min-h-screen items-center justify-center px-6">
      <div className="flex flex-col items-center gap-4 text-center">{children}</div>
    </div>
  );
}

export function CenteredMessage({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div className="flex min-h-screen items-center justify-center px-6 text-sm text-ink-muted">
      {children}
    </div>
  );
}

export function formatDate(timestamp: number): string {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    timeZone: 'UTC',
  }).format(new Date(timestamp));
}
